import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const coreValues = [
  {
    icon: 'ri-heart-line',
    title: 'People First',
    description: 'We genuinely care for our associates and clients, putting people at the center of every decision we make.',
  },
  {
    icon: 'ri-team-line',
    title: 'Diversity & Equality',
    description: 'We celebrate differences and foster an inclusive environment where every voice is heard and valued.',
  },
  {
    icon: 'ri-star-line',
    title: 'Customer Delight',
    description: 'We go beyond satisfaction — we wow our customers with exceptional service and outstanding results.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Adaptability',
    description: 'We embrace change and remain flexible to meet the evolving needs of our customers and the market.',
  },
  {
    icon: 'ri-code-s-slash-line',
    title: 'Technical Excellence',
    description: 'We hold ourselves to the highest technical standards, continuously learning and innovating.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Trusted Partnership',
    description: 'We build long-term relationships grounded in transparency, integrity, and mutual success.',
  },
];

const culturePerks = [
  {
    icon: 'ri-time-line',
    title: 'Flexible Work Arrangements',
    description: 'We trust our team to deliver results on their own terms with flexible schedules and remote options.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'World-Class Facilities',
    description: 'Our offices are the envy of even the biggest multinationals — designed for creativity and comfort.',
  },
  {
    icon: 'ri-gamepad-line',
    title: 'Fun Team Events',
    description: 'Regular team-building activities and social events that bring our people together and keep spirits high.',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Continuous Learning',
    description: 'Periodic training programs and skill development opportunities to keep our team at the cutting edge.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Personal Support',
    description: 'Unflinching support during personal emergencies — because we truly care about our people.',
  },
  {
    icon: 'ri-trophy-line',
    title: 'Record Low Attrition',
    description: 'Our consistently high associate engagement speaks volumes about the culture we\'ve built together.',
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[520px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/b73c340f4addba728354833ce330ed8b.jpg"
            alt="About Unitiech"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A5F]/90 via-[#1E3A5F]/75 to-[#0D9488]/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full text-center">
          <span className="text-[#14B8A6] text-xs uppercase tracking-widest font-semibold mb-4 block">
            WHO WE ARE
          </span>
          <h1 className="text-6xl font-bold text-white leading-tight mb-6">
            About Uniti Tech
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
            A fast-growing IT services and product company on a mission to deliver world-class technology solutions.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-12 h-1 bg-[#14B8A6] mb-5"></div>
              <h2 className="text-4xl font-bold text-[#1E3A5F] mb-6">Our Story</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Uniti Tech is a fast-growing IT services and product company. We started off as an IT services company and are proud to add product development to our core mission.&nbsp;
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From our roots in IT services, we have grown into a full-spectrum technology partner — helping businesses modernize, scale, and thrive in a digital-first world. Our team combines deep technical expertise with a genuine passion for customer success.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/services"
                  className="px-8 py-3 bg-[#1E3A5F] text-white rounded-full font-semibold text-sm hover:bg-[#14B8A6] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Our Services
                </Link>
                <Link
                  to="/engagement"
                  className="px-8 py-3 border-2 border-[#1E3A5F] text-[#1E3A5F] rounded-full font-semibold text-sm hover:bg-[#1E3A5F] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Engagement Model
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[420px] rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20IT%20technology%20team%20meeting%20in%20modern%20office%20diverse%20group%20of%20engineers%20and%20consultants%20discussing%20project%20on%20large%20screen%20whiteboard%20collaboration%20session%20bright%20contemporary%20workspace%20teal%20accents&width=700&height=420&seq=about-story1&orientation=landscape"
                  alt="Uniti Tech Team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 md:order-1">
              <div className="w-full h-[400px] rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=business%20mission%20vision%20strategy%20concept%20modern%20office%20setting%20professional%20team%20planning%20on%20glass%20board%20with%20sticky%20notes%20roadmap%20goals%20objectives%20clean%20minimalist%20environment%20navy%20and%20teal%20color%20palette&width=700&height=400&seq=about-mission1&orientation=landscape"
                  alt="Our Mission"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -top-6 -right-6 bg-[#1E3A5F] rounded-2xl p-6 shadow-lg">
                <i className="ri-focus-3-line text-3xl text-[#14B8A6]"></i>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="w-12 h-1 bg-[#14B8A6] mb-5"></div>
              <h2 className="text-4xl font-bold text-[#1E3A5F] mb-6">Our Mission</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Our mission is to be a <strong className="text-[#1E3A5F]">trusted partner</strong> for providing world-class IT services and products to our customers as a means to delivering their business outcomes.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-checkbox-circle-line', text: 'World-class IT services tailored to your needs' },
                  { icon: 'ri-checkbox-circle-line', text: 'Innovative product development driving real outcomes' },
                  { icon: 'ri-checkbox-circle-line', text: 'Long-term partnerships built on trust and results' },
                  { icon: 'ri-checkbox-circle-line', text: 'Commitment to excellence in every engagement' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#14B8A6] text-lg`}></i>
                    </div>
                    <p className="text-sm text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Model Teaser */}
      <section className="py-24 bg-gradient-to-br from-[#1E3A5F] via-[#1E3A5F] to-[#0D9488]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="w-12 h-1 bg-[#14B8A6] mb-5 mx-auto"></div>
            <h2 className="text-4xl font-bold text-white mb-4">Our Engagement Model</h2>
            <p className="text-lg text-white/75 max-w-3xl mx-auto leading-relaxed">
              We are truly passionate in helping our customers succeed by providing world-class IT services through our unique engagement models while keeping costs optimal. Our ability to keep overheads to a bare minimum allows us to offer a highly competitive cost model.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              { icon: 'ri-money-dollar-circle-line', title: 'Competitive Pricing', desc: 'Minimal overheads translate directly into cost savings for our clients without compromising quality.' },
              { icon: 'ri-settings-3-line', title: 'Flexible Models', desc: 'Tailored engagement structures that adapt to your project scope, timeline, and budget requirements.' },
              { icon: 'ri-award-line', title: 'World-Class Delivery', desc: 'Consistent, high-quality outcomes delivered by our expert teams across every engagement.' },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-[#14B8A6]/20 rounded-full mx-auto mb-5">
                  <i className={`${item.icon} text-2xl text-[#14B8A6]`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/engagement"
              className="inline-flex items-center gap-3 px-10 py-4 bg-white text-[#1E3A5F] rounded-full font-bold text-sm hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Learn More About Our Engagement Model
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="w-12 h-1 bg-[#14B8A6] mb-5 mx-auto"></div>
            <h2 className="text-4xl font-bold text-[#1E3A5F] mb-4">Our Core Values</h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">
              The principles that guide everything we do — from how we serve our clients to how we care for our people.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((value, i) => (
              <div
                key={i}
                className="bg-[#F8F9FA] rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#1E3A5F] rounded-xl mb-5">
                  <i className={`${value.icon} text-xl text-[#14B8A6]`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1E3A5F] mb-3">{value.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
            <div>
              <div className="w-12 h-1 bg-[#14B8A6] mb-5"></div>
              <h2 className="text-4xl font-bold text-[#1E3A5F] mb-6">Our Culture</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                We have worked very hard to establish a world-class culture based on our core values. Our consistently high associate engagement and record low attrition is a testimony to our efforts in constantly nurturing our culture.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                We truly take care of our associates — because when our people thrive, our clients thrive too.
              </p>
              <div className="w-full h-[320px] rounded-3xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=happy%20diverse%20technology%20company%20team%20celebrating%20success%20in%20modern%20office%20space%20fun%20team%20building%20event%20group%20photo%20smiling%20professionals%20casual%20atmosphere%20vibrant%20colorful%20contemporary%20workspace%20teal%20accents&width=700&height=320&seq=about-culture1&orientation=landscape"
                  alt="Uniti Tech Culture"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5">
              {culturePerks.map((perk, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl p-5 flex items-start gap-5 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#CCFBF1] rounded-lg flex-shrink-0">
                    <i className={`${perk.icon} text-lg text-[#0D9488]`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#1E3A5F] mb-1">{perk.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{perk.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-[#1E3A5F] mb-5">Ready to Work With Us?</h2>
          <p className="text-base text-gray-500 mb-10 max-w-xl mx-auto">
            Let's explore how Uniti Tech can help you achieve your business goals with world-class IT services and products.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="px-10 py-4 bg-[#1E3A5F] text-white rounded-full font-semibold text-sm hover:bg-[#14B8A6] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get In Touch
            </Link>
            <Link
              to="/services"
              className="px-10 py-4 border-2 border-[#1E3A5F] text-[#1E3A5F] rounded-full font-semibold text-sm hover:bg-[#1E3A5F] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
